import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { ArrowRight, CaretRight } from '@phosphor-icons/react';
import { getAllPosts } from '../data/blogPosts';

export default function BlogPage() {
  const posts = getAllPosts();
  const navigate = useNavigate();

  const blogJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'RoofMate Blog',
    url: 'https://roofmate.app/blog',
    description: 'Guides, comparisons, and how-tos on roof measurement, satellite imagery, and site logistics for Australian roofers and builders.',
    blogPost: posts.map(p => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: `https://roofmate.app/blog/${p.slug}`,
      datePublished: p.datePublished,
      author: { '@type': 'Organization', name: p.author || 'RoofMate' },
      image: p.coverImage,
      description: p.description,
    })),
  };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-index-page">
      <Seo
        title="Blog — Roof Measurement Guides and Comparisons"
        description="Guides, comparisons, and how-tos on roof measurement, satellite imagery, and site logistics for Australian roofers and builders."
        path="/blog"
        keywords="roof measurement blog, satellite roof measurement guide, aerial roof measurement, roofing software tips"
        jsonLd={blogJsonLd}
      />

      {/* Navbar (simple — reuses landing-page styling) */}
      <nav className="sticky top-0 z-50 bg-card/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="/logo-full.png" alt="RoofMate logo" className="h-8 sm:h-[2.4rem] object-contain" width="180" height="40" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm font-medium text-muted-foreground hover:text-foreground">Home</Link>
            <Button onClick={() => navigate('/login')} variant="outline" className="rounded-md font-medium text-sm h-9 px-4" data-testid="blog-nav-login">Sign In</Button>
            <Button onClick={() => navigate('/')} className="rounded-md font-medium text-sm h-9 px-4" data-testid="blog-nav-signup">Get Started <ArrowRight size={14} className="ml-1" weight="bold" /></Button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-3">RoofMate Blog</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Roof measurement, satellite imagery & site planning</h1>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-2xl">Practical guides for Australian roofers and builders — how to measure faster, quote more accurately, and plan safer sites.</p>
        </div>
      </header>

      {/* Posts list */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {posts.map(post => (
            <Link
              to={`/blog/${post.slug}`}
              key={post.slug}
              className="group block bg-card rounded-xl overflow-hidden border border-border hover:border-primary/40 hover:shadow-card transition-all"
              data-testid={`blog-card-${post.slug}`}
            >
              <div className="aspect-[16/9] overflow-hidden bg-secondary">
                <img
                  src={post.coverImage}
                  alt={post.coverAlt}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  loading="lazy"
                  width="1600"
                  height="900"
                />
              </div>
              <div className="p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                  <span>{new Date(post.datePublished).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <span aria-hidden="true">·</span>
                  <span>{post.readingTime}</span>
                </div>
                <h2 className="text-lg sm:text-xl font-bold leading-snug group-hover:text-primary transition-colors">{post.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{post.description}</p>
                <p className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                  Read article <CaretRight size={12} weight="bold" />
                </p>
              </div>
            </Link>
          ))}
        </div>
      </main>

      {/* Footer CTA */}
      <section className="py-12 sm:py-16 bg-card border-t border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Ready to measure your first roof?</h2>
          <p className="text-sm text-muted-foreground mb-6">7-day free trial · Cancel anytime</p>
          <Button onClick={() => navigate('/')} className="rounded-md font-medium text-sm h-10 px-8 shadow-lg shadow-primary/25" data-testid="blog-cta-signup">
            Start Free Trial <ArrowRight size={16} className="ml-1.5" weight="bold" />
          </Button>
        </div>
      </section>
    </div>
  );
}
